import { useEffect, useRef, useState } from "react";

import {
  cashFlowPlanningSourceLabel,
  physicalFinancialLabel,
} from "./cashFlowPlanning";
import { updateCashFlowPlanningSource } from "./cashFlowPlanningApi";

const STATE_BADGE = Object.freeze({
  selection_required: "Escolha pendente",
  needs_adjustment: "Em ajuste",
  unavailable: "Indisponível",
  not_applicable: "Indisponível",
});

// Uma fonte ainda não escolhida é um passo de configuração, não um estado de rotina: o
// gatilho recolhido não tem o que resumir, então o popover começa aberto. Os demais
// estados de atenção continuam recolhidos, sinalizados no próprio gatilho.
const OPEN_BY_DEFAULT = new Set(["selection_required"]);
const NEEDS_ATTENTION = new Set([
  "selection_required",
  "needs_adjustment",
  "unavailable",
  "not_applicable",
]);

const copyFor = (policy) => {
  if (
    policy?.requestedSource === "combined" &&
    policy?.state === "needs_adjustment"
  ) {
    return {
      title: "Visão completa: desembolso em ajuste",
      description: `${physicalFinancialLabel(policy)} continua disponível. O desembolso aparece em paralelo e mantém a jornada para chegar a 100% de cobertura.`,
    };
  }
  if (policy?.state === "needs_adjustment") {
    return {
      title: "Desembolso escolhido: conclua os ajustes",
      description:
        "Enquanto a cobertura não chegar a 100%, o desembolso não será usado silenciosamente como previsão de caixa.",
    };
  }
  if (policy?.state === "ready" && policy.effectiveSource === "disbursement") {
    return {
      title: "Previsão de caixa pelo cronograma de desembolso",
      description:
        "A organização usa o desembolso reconciliado como fonte canônica da projeção.",
    };
  }
  if (
    policy?.state === "ready" &&
    policy.effectiveSource === "physical_financial"
  ) {
    return {
      title: "Previsão de caixa pelo físico-financeiro",
      description:
        "A organização usa o previsto mensal e não precisa ajustar o cronograma de desembolso.",
    };
  }
  if (policy?.state === "ready" && policy.effectiveSource === "combined") {
    return {
      title: "Visão completa da previsão de caixa",
      description: `${physicalFinancialLabel(policy)} e o desembolso reconciliado aparecem juntos, sem soma ou mistura entre as curvas.`,
    };
  }
  if (policy?.state === "unavailable") {
    return {
      title: "Fonte de previsão temporariamente indisponível",
      description:
        "A fonte escolhida não possui snapshot utilizável. Nenhuma outra fonte foi aplicada como fallback.",
    };
  }
  return {
    title: "Como esta organização quer projetar o caixa?",
    description:
      "Escolha uma fonte única ou a visão completa. Na visão completa, previsto e desembolso permanecem identificados e nunca são somados.",
  };
};

const CashFlowPlanningSourceCard = ({ policy, projectId, onUpdated }) => {
  const [editing, setEditing] = useState(
    policy?.state === "selection_required",
  );
  const [pendingSource, setPendingSource] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(false);
  const [open, setOpen] = useState(OPEN_BY_DEFAULT.has(policy?.state));
  const containerRef = useRef(null);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    const onPointerDown = (event) => {
      if (!containerRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("mousedown", onPointerDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("mousedown", onPointerDown);
    };
  }, [open]);
  if (!policy) return null;

  const copy = copyFor(policy);
  const showChoices =
    policy.canManage && (editing || policy.state === "selection_required");
  const needsAttention = NEEDS_ATTENTION.has(policy.state);

  const save = async () => {
    if (!pendingSource || saving) return;
    setSaving(true);
    setError(false);
    const result = await updateCashFlowPlanningSource(projectId, pendingSource);
    if (!mounted.current) return;
    if (!result) {
      setError(true);
      setSaving(false);
      return;
    }
    setSaving(false);
    setEditing(false);
    setPendingSource(null);
    await onUpdated?.(result.source);
  };

  return (
    <div className="cash-flow-planning-source-chip-wrapper" ref={containerRef}>
      <button
        type="button"
        className={`cash-flow-planning-source-chip cash-flow-planning-source-chip--${policy.state}${needsAttention ? " cash-flow-planning-source-chip--attention" : ""}`}
        aria-label="Política de previsão de caixa da organização"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <span className="cash-flow-planning-source-chip__label">Política</span>
        <strong>
          {policy.requestedSource
            ? cashFlowPlanningSourceLabel(policy.requestedSource)
            : "Não definida"}
        </strong>
        {needsAttention ? (
          <span className="cash-flow-planning-source-chip__condition">
            {STATE_BADGE[policy.state] ?? "Atenção"}
          </span>
        ) : null}
        <span aria-hidden="true" className="cash-flow-planning-source-chip__caret">
          {open ? "▲" : "▼"}
        </span>
      </button>
      {open ? (
      <section className={`cash-flow-planning-source ${policy.state}`}>
        <div className="cash-flow-planning-source__header">
          <div>
            <span className="coverage-eyebrow">Política da organização</span>
            <h3>{copy.title}</h3>
            <p>{copy.description}</p>
          </div>
          {policy.requestedSource ? (
            <span className="cash-flow-planning-source__badge">
              {cashFlowPlanningSourceLabel(policy.requestedSource)}
            </span>
          ) : null}
        </div>

        {!policy.canManage && policy.state === "selection_required" ? (
          <p className="cash-flow-planning-source__notice">
            Peça a um administrador da empresa para escolher a fonte da
            organização.
          </p>
        ) : null}

        {showChoices ? (
          <div className="cash-flow-planning-source__choices">
            <button
              type="button"
              aria-pressed={pendingSource === "disbursement"}
              className={
                pendingSource === "disbursement" ? "selected" : undefined
              }
              disabled={!policy.availableSources?.disbursement || saving}
              onClick={() => setPendingSource("disbursement")}
            >
              <strong>Usar cronograma de desembolso</strong>
              <span>Exige 100% de cobertura e libera a jornada de ajustes.</span>
            </button>
            <button
              type="button"
              aria-pressed={pendingSource === "combined"}
              className={pendingSource === "combined" ? "selected" : undefined}
              disabled={!policy.availableSources?.combined || saving}
              onClick={() => setPendingSource("combined")}
            >
              <strong>Usar visão completa</strong>
              <span>
                Previsto e desembolso aparecem juntos, sem soma, com suas fontes identificadas.
              </span>
            </button>
            <button
              type="button"
              aria-pressed={pendingSource === "physical_financial"}
              className={
                pendingSource === "physical_financial" ? "selected" : undefined
              }
              disabled={!policy.availableSources?.physicalFinancial || saving}
              onClick={() => setPendingSource("physical_financial")}
            >
              <strong>Usar previsto físico-financeiro</strong>
              <span>
                Usa o previsto mensal e ignora pendências do desembolso.
              </span>
            </button>
          </div>
        ) : null}

        {showChoices && pendingSource ? (
          <div className="cash-flow-planning-source__confirm">
            <p>
              Esta escolha vale para todas as obras da organização:{" "}
              {cashFlowPlanningSourceLabel(pendingSource)}.
            </p>
            <div>
              {policy.requestedSource ? (
                <button
                  type="button"
                  className="button button-secondary small"
                  disabled={saving}
                  onClick={() => {
                    setPendingSource(null);
                    setEditing(false);
                  }}
                >
                  Cancelar
                </button>
              ) : null}
              <button
                type="button"
                className="button button-accent small"
                disabled={saving}
                onClick={save}
              >
                {saving ? "Salvando…" : "Confirmar fonte"}
              </button>
            </div>
          </div>
        ) : null}

        {policy.canManage && policy.requestedSource && !showChoices ? (
          <button
            type="button"
            className="cash-flow-planning-source__change"
            onClick={() => setEditing(true)}
          >
            Alterar fonte da organização
          </button>
        ) : null}

        {error ? (
          <p className="alert alert-error" role="alert">
            Não foi possível salvar a fonte. Tente novamente.
          </p>
        ) : null}
      </section>
      ) : null}
    </div>
  );
};

export default CashFlowPlanningSourceCard;
